import { DateTime } from 'luxon'
import { computed, type Ref } from 'vue'
import Holidays from 'date-holidays'

export enum CalendarMode {
  Weekdays,
  Weekends,
}

const hd = new Holidays('US')

/** BE Holidays, which are defined as:
 * - New Years Day (January 1st)
 * - Memorial Day (last Monday in May)
 * - Independence Day (July 4th)
 * - Labor Day (first Monday in September)
 * - Thanksgiving Day (fourth Thursday in November)
 * - Christmas Day (December 25th)
 */
const OBSERVED_HOLIDAYS = [
  "New Year's Day",
  'Memorial Day',
  'Independence Day',
  'Labor Day',
  'Thanksgiving Day',
  'Christmas Day',
]

/** Pulls OBSERVED_HOLIDAYS from `date-holidays` */
const getHolidayList = (year: number) => {
  const yearHolidays = hd.getHolidays(year)

  // Filter and convert them to Luxon objects
  return yearHolidays
    .filter((h) => OBSERVED_HOLIDAYS.includes(h.name))
    .map((h) => {
      // h.date is a string like "2026-05-25 00:00:00"
      return { date: DateTime.fromSQL(h.date), name: h.name }
    })
}

function validDateTime(value: DateTime | string | null): DateTime<true> | null {
  if (!value) return null
  if (typeof value === 'string') {
    const parsed = DateTime.fromISO(value)
    return parsed.isValid ? parsed : null
  }
  return DateTime.isDateTime(value) && value.isValid ? value : null
}

/**
 * Generates a range of dates between startDate and endDate based on mode (only weekdays or weekends),
 * its headers and a validity. Params support null for composable-friendly params.
 *
 * @param startDate can be either ISO string, luxon DateTime or null
 * @param endDate can be either ISO string, luxon DateTime or null
 * @param mode weekdays or weekends (CalendarMode enum)
 * @param marginWeeks number of weeks to add as margin before startDate and after endDate
 * @returns array of dates between startDate and endDate based on mode (only weekdays or weekends)
 */
export function useWeeksCalendar(
  startDate: Ref<DateTime | string | null>,
  endDate: Ref<DateTime | string | null>,
  mode?: Ref<CalendarMode>,
  marginWeeks = 0
) {
  const startDt = computed(() => validDateTime(startDate.value))
  const endDt = computed(() => validDateTime(endDate.value))

  const holidaysMap = computed(() => {
    if (!startDt.value || !endDt.value) return new Map()

    const map = new Map<string, { name: string; date: DateTime }>()

    for (let year = startDt.value.year; year <= endDt.value.year; year++) {
      const list = getHolidayList(year)
      list.forEach((h) => {
        if (h.date.isValid) {
          map.set(h.date.toISODate(), h)
        }
      })
    }
    return map
  })

  // Generate dates between startIsoDate and endIsoDate based on mode
  const dates = computed(() => {
    const start = startDt.value
    const end = endDt.value

    if (!start || !end) return []

    // Find Monday of start date
    const startMonday = start.startOf('week').minus({ weeks: marginWeeks })

    // Find Sunday of end date
    const endSunday = end.endOf('week').plus({ weeks: marginWeeks })

    const dates = []
    let current = startMonday

    const targettingWeekdays = !mode || mode.value === CalendarMode.Weekdays

    while (current <= endSunday) {
      const holiday = holidaysMap.value.get(current.toISODate())
      // Only add weekdays
      if (targettingWeekdays && current.weekday <= 5) {
        if (current.isValid) dates.push({ date: current, holiday })
      }
      // Only add weekends
      if (!targettingWeekdays && (current.weekday >= 6 || holiday)) {
        if (current.isValid) dates.push({ date: current, holiday })
      }
      current = current.plus({ days: 1 })
    }

    return dates
  })

  const valid = computed(() => dates.value.length > 0)

  const hasWeekdayHoliday = computed(
    () => mode?.value === CalendarMode.Weekends && dates.value.some((d) => d.holiday && !d.date.isWeekend)
  )

  // If !mode defaults to weekdays
  const headers = computed(() => {
    if (!mode || mode.value === CalendarMode.Weekdays) return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']

    // If weekends mode, check if there are holidays among weekdays in dates
    if (hasWeekdayHoliday.value) {
      return ['Holidays', 'Sat', 'Sun']
    }

    return ['Sat', 'Sun']
  })

  return { headers, dates, valid, hasWeekdayHoliday }
}
